import { z } from 'zod';

import { uuidSchema } from '../common/primitives';

/**
 * Die Eigentumsgrenze eines Kundenzugangs — für alle Zugangsarten dieselbe.
 *
 * ## Warum das eine eigene Datei ist
 *
 * Seit M4a hat eine Site Datenbanken, seit M4c zusätzlich SFTP-Konten. Beide
 * beantworten dieselbe Frage: „Gehört dieser Name zu dieser Site — darf ich ihn
 * anlegen, und darf ich ihn abräumen?" Zwei Kopien dieser Regel wären zwei
 * Gelegenheiten, sie unterschiedlich auszulegen; die Folge wäre ein Zugriff auf
 * die Daten eines fremden Kunden, und zwar lautlos.
 *
 * Der Präfix ist **abgeleitet und wird nie übertragen**: Master und Agent
 * berechnen ihn beide aus der Site-ID. Damit gibt es keine Nachricht, in der er
 * falsch stehen könnte, und der Agent kann unabhängig vom Master prüfen, ob
 * eine Spezifikation ihm einen fremden Zugang unterschieben will.
 *
 * ## Der Zeichenvorrat
 *
 * `[a-z0-9]`, nichts weiter. Der Grund kommt aus der Datenbank und ist in
 * `database.ts` ausführlich begründet: **In einem `GRANT` sind `_` und `%`
 * Platzhalter — auch in Backticks.** Ein Name mit Unterstrich verteilte Rechte
 * an jede passende Datenbank des Servers. Für SFTP-Benutzernamen gilt derselbe
 * Vorrat, aus einem zweiten Grund: Ein Benutzername wird bei SFTPGo Teil eines
 * Pfads und einer Filterregel, und ein Name ohne Sonderzeichen kann dort
 * nirgends etwas anderes bedeuten, als er aussieht.
 */

// -- Zeichenvorrat -----------------------------------------------------------

/**
 * Bezeichner eines Kundenzugangs: Datenbankname, Datenbankbenutzer,
 * SFTP-Benutzername.
 *
 * Beginnt mit einem Kleinbuchstaben, danach ausschließlich Kleinbuchstaben und
 * Ziffern. Kein Unterstrich (der wäre im `GRANT` ein Platzhalter), kein
 * Bindestrich (der zwänge zu Backticks an jeder Stelle), keine Großbuchstaben
 * (Datenbanknamen sind unter Linux von der Groß-/Kleinschreibung abhängig,
 * unter macOS nicht — ein Unterschied, der beim Umzug einer Site zuschlägt).
 *
 * Höchstens 32 Zeichen: Das ist die Obergrenze für Benutzernamen in MySQL.
 * MariaDB erlaubt seit 10.6 achtzig, aber ein Name, der nur auf einem der
 * beiden Server funktioniert, ist eine Falle für später.
 */
export const ACCESS_IDENTIFIER_PATTERN = /^[a-z][a-z\d]{1,31}$/;

export const accessIdentifierSchema = z.string().regex(ACCESS_IDENTIFIER_PATTERN, {
  message:
    'Muss mit einem Kleinbuchstaben beginnen und darf danach nur Kleinbuchstaben und Ziffern enthalten (2 bis 32 Zeichen). Unterstrich und Prozentzeichen sind ausgeschlossen, weil sie in einem GRANT Platzhalter wären.',
});

/**
 * Zeichenvorrat eines erzeugten Kundenpassworts.
 *
 * Bewusst ohne Anführungszeichen, Backslash und Leerzeichen: Das Passwort
 * landet in `IDENTIFIED BY '…'`, und dort ist ein einzelnes Anführungszeichen
 * ein Ausbruch aus der Zeichenkette. Da wir das Passwort selbst erzeugen,
 * kostet die Einschränkung nichts — sie nimmt der Anweisung nur die
 * Möglichkeit, missverstanden zu werden.
 *
 * Ebenfalls ausgeschlossen: `$`, `` ` `` und `!`. Der Kunde fügt dieses
 * Passwort in Konfigurationsdateien und Kommandozeilen ein, und jedes dieser
 * Zeichen wird dort von einer Shell ausgewertet.
 */
export const ACCESS_PASSWORD_PATTERN = /^[A-Za-z\d._~-]{24,64}$/;

export const accessPasswordSchema = z.string().regex(ACCESS_PASSWORD_PATTERN, {
  message:
    'Muss 24 bis 64 Zeichen aus Buchstaben, Ziffern, Punkt, Unterstrich, Tilde und Bindestrich enthalten.',
});

// -- Der Präfix --------------------------------------------------------------

/** Erkennungszeichen aller von uns verwalteten Zugänge. */
export const ACCESS_NAME_PREFIX = 'ks';

/** Wie viele Zeichen der Site-ID in den Präfix eingehen. */
const SITE_ID_FRAGMENT_LENGTH = 12;

/**
 * Der Namenspräfix aller Zugänge einer Site.
 *
 * Zwölf Hexadezimalstellen sind 48 Bit. Auf einem Server mit ein paar hundert
 * Sites ist eine Kollision damit ausgeschlossen — und selbst sie wäre nur eine
 * abgewiesene Bestellung, weil Datenbank- und Benutzernamen systemweit
 * eindeutig sind.
 */
export function sitePrefixFor(siteId: string): string {
  const compact = uuidSchema.parse(siteId).replaceAll('-', '');

  return `${ACCESS_NAME_PREFIX}${compact.slice(0, SITE_ID_FRAGMENT_LENGTH)}`;
}

/**
 * Gehört dieser Name zu dieser Site?
 *
 * Der Agent beantwortet damit zwei Fragen mit derselben Regel: „darf ich das
 * anlegen?" und „darf ich das abräumen?". Ohne diese Bindung wäre ein Fehler im
 * Master — oder ein übernommener Master — in der Lage, einer Site den Zugang
 * einer anderen zuzuweisen.
 */
export function belongsToSite(siteId: string, identifier: string): boolean {
  return identifier.startsWith(sitePrefixFor(siteId));
}

/**
 * Prüft die laufende Nummer eines Zugangs.
 *
 * `bezeichnung` steht in der Meldung, damit der Betreiber sieht, welche Art von
 * Zugang gemeint ist — die Zahl allein sagt ihm nichts.
 */
export function assertAccessOrdinal(ordinal: number, max: number, bezeichnung: string): number {
  if (!Number.isInteger(ordinal) || ordinal < 1 || ordinal > max) {
    throw new Error(
      `Die laufende Nummer ${bezeichnung} muss zwischen 1 und ${String(max)} liegen, war aber ${String(ordinal)}.`,
    );
  }

  return ordinal;
}
